import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Layout from '../components/Layout';

const Accounting = () => {
  const [transactions, setTransactions] = useState([]);
  const [rentPayments, setRentPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [financialSummary, setFinancialSummary] = useState({
    totalIncome: 0,
    totalExpenses: 0,
    netIncome: 0,
    collectionsRate: 0
  });

  useEffect(() => {
    const fetchAccountingData = async () => {
      // Fetch transactions
      const { data: transactionData } = await supabase
        .from('transactions')
        .select(`
          *,
          properties(name),
          units(unit_number)
        `)
        .order('transaction_date', { ascending: false });
      setTransactions(transactionData || []);

      // Fetch rent payments
      const { data: paymentData } = await supabase
        .from('rent_payments')
        .select(`
          *,
          leases(
            rent_amount,
            units(unit_number, properties(name)),
            residents(first_name, last_name)
          )
        `)
        .order('due_date', { ascending: false });
      setRentPayments(paymentData || []);

      // Calculate financial summary
      calculateFinancialSummary(transactionData || [], paymentData || []);
    };
    
    fetchAccountingData();
  }, []);



  const calculateFinancialSummary = (transactions, payments) => {
    const income = transactions
      .filter(t => t.transaction_type === 'credit')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const expenses = transactions
      .filter(t => t.transaction_type === 'debit')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);

    const paidPayments = payments.filter(p => p.status === 'paid').length;
    const totalPayments = payments.length;
    const collectionsRate = totalPayments > 0 ? (paidPayments / totalPayments) * 100 : 0;

    setFinancialSummary({
      totalIncome: income,
      totalExpenses: expenses,
      netIncome: income - expenses,
      collectionsRate
    });
  };

  return (
    <Layout>
      <div style={{ padding: '20px' }}>
        <h2>Property Accounting</h2>
        
        <div style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
          <button 
            onClick={() => setActiveTab('dashboard')}
            className={`btn ${activeTab === 'dashboard' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Dashboard
          </button>
          <button 
            onClick={() => setActiveTab('rent')}
            className={`btn ${activeTab === 'rent' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Rent Collection
          </button>
          <button 
            onClick={() => setActiveTab('transactions')}
            className={`btn ${activeTab === 'transactions' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Transactions
          </button>
          <button 
            onClick={() => setActiveTab('reports')}
            className={`btn ${activeTab === 'reports' ? 'btn-primary' : 'btn-secondary'}`}
          >
            Reports
          </button>
        </div>

        {activeTab === 'dashboard' && (
          <div>
            <h3>Financial Dashboard</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Income</h4>
                <p style={{ fontSize: '24px', color: '#28a745', fontWeight: 'bold' }}>
                  ${financialSummary.totalIncome.toLocaleString()}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Total Expenses</h4>
                <p style={{ fontSize: '24px', color: '#dc3545', fontWeight: 'bold' }}>
                  ${financialSummary.totalExpenses.toLocaleString()}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Net Income</h4>
                <p style={{ fontSize: '24px', color: '#007bff', fontWeight: 'bold' }}>
                  ${financialSummary.netIncome.toLocaleString()}
                </p>
              </div>
              <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', textAlign: 'center' }}>
                <h4>Collections Rate</h4>
                <p style={{ fontSize: '24px', color: '#17a2b8', fontWeight: 'bold' }}>
                  {financialSummary.collectionsRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'rent' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Rent Collection</h3>
              <button className="btn btn-primary">Record Payment</button>
            </div>
            
            <div style={{ display: 'grid', gap: '15px' }}>
              {rentPayments.map(payment => (
                <div key={payment.id} style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4>{payment.leases?.properties?.name} - Unit {payment.leases?.units?.unit_number}</h4>
                      <p style={{ color: '#666' }}>
                        {payment.leases?.residents?.first_name} {payment.leases?.residents?.last_name}
                      </p>
                      <p>Amount: ${payment.amount} | Due: {new Date(payment.due_date).toLocaleDateString()}</p>
                      {payment.paid_date && <p>Paid: {new Date(payment.paid_date).toLocaleDateString()}</p>}
                    </div>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      background: payment.status === 'paid' ? '#d4edda' : payment.status === 'late' ? '#f8d7da' : '#fff3cd',
                      color: payment.status === 'paid' ? '#155724' : payment.status === 'late' ? '#721c24' : '#856404'
                    }}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>Transactions</h3>
              <button className="btn btn-primary">Add Transaction</button>
            </div>
            
            <div style={{ background: 'white', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ background: '#f8f9fa' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Description</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Property</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #ddd' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.slice(0, 10).map(transaction => (
                    <tr key={transaction.id}>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {new Date(transaction.transaction_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {transaction.description}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {transaction.properties?.name}
                      </td>
                      <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                        {transaction.category}
                      </td>
                      <td style={{ 
                        padding: '12px', 
                        borderBottom: '1px solid #eee', 
                        textAlign: 'right',
                        color: transaction.transaction_type === 'credit' ? '#28a745' : '#dc3545'
                      }}>
                        {transaction.transaction_type === 'credit' ? '+' : '-'}${transaction.amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div>
            <h3>Financial Reports</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <button className="btn btn-primary">Income Statement</button>
              <button className="btn btn-primary">Cash Flow Report</button>
              <button className="btn btn-primary">Rent Roll</button>
              <button className="btn btn-primary">Owner Statement</button>
              <button className="btn btn-primary">Tax Report</button>
              <button className="btn btn-primary">Vacancy Report</button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Accounting;